import React from 'react';

import { BottomCard, BottomCardForm } from '@/components/shared/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';

import styles from './UpgradeGateSheet.module.scss';

export function useGateAccepted(gateName: string): boolean {
	const key = `gate_accepted_${gateName}`;
	const [accepted, setAccepted] = React.useState(() =>
		Boolean(localStorage.getItem(key)),
	);

	React.useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<{ gateName: string }>).detail;
			if (detail.gateName === gateName) {
				setAccepted(true);
			}
		};
		window.addEventListener('gate:accepted', handler);
		return () => window.removeEventListener('gate:accepted', handler);
	}, [gateName]);

	return accepted;
}

interface UpgradeGateSheetProps {
	open: boolean;
	onClose: () => void;
	heading: string;
	body: string;
	gateName: string; // snake_case: ai_setlists | services | admins | reports_date_range | private_songs
	onSkip: () => void;
	rememberAcceptance?: boolean;
}

export const UpgradeGateSheet: React.FC<UpgradeGateSheetProps> = ({
	open,
	onClose,
	heading,
	body,
	gateName,
	onSkip,
	rememberAcceptance,
}) => {
	const trackSkipped = useAnalyticsEvent('Upgrade_Gate_Skipped');

	React.useEffect(() => {
		if (open && rememberAcceptance) {
			const key = `gate_accepted_${gateName}`;
			if (localStorage.getItem(key)) {
				onSkip();
				onClose();
			}
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!open) {
		return null;
	}

	return (
		<BottomCard title={heading} onClose={onClose} hasCloseButton>
			<BottomCardForm>{body}</BottomCardForm>
			<div className={styles.footer}>
				<AsaphButton
					color="green"
					label="Continue"
					onClick={() => {
						trackSkipped({ gate: gateName });
						if (rememberAcceptance) {
							localStorage.setItem(
								`gate_accepted_${gateName}`,
								'1',
							);
							window.dispatchEvent(
								new CustomEvent('gate:accepted', {
									detail: { gateName },
								}),
							);
						}
						onSkip();
						onClose();
					}}
					fullWidth
				/>
				<div className={styles.disclaimer}>
					Continue for free. In future, this feature will be part of a
					paid plan.
				</div>
			</div>
		</BottomCard>
	);
};
